import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '@/lib/teaching/teacher-api';
import { TeachingPage, Card } from '@/components/teaching/ui';
import { Btn, ErrorLine } from '@/components/teaching/form';
import { Plus } from 'lucide-preact';

// "My agents": what each Telegram agent should do, in the teacher's words.
// Saved as he types; Ali reads it with `teacher-cli briefs` and turns it
// into each agent's instructions.

type Q = 'handle' | 'never' | 'when' | 'tone' | 'examples' | 'last_week';
type Brief = { answers: Partial<Record<Q | 'name', string>>; updated_at: string | null };
type AgentInfo = { id: string; name: string; sub: string; extra?: boolean };

/** Agents he adds himself (up to 8) carry an `x-` id and their own name. */
const MAX_EXTRA = 8;
const newExtraId = () => `x-${Math.random().toString(36).slice(2, 10)}`;

const AGENTS: AgentInfo[] = [
  { id: 'main', name: 'Manager', sub: 'Your first stop. Hands work to the others.' },
  { id: 'teaching', name: 'Teaching', sub: 'Classes, marking, students.' },
  { id: 'research', name: 'Research', sub: 'Looks things up and sums them up.' },
  { id: 'personal', name: 'Personal', sub: 'Your life outside school. Private chat only.' },
];

const QUESTIONS: { id: Q; label: string; hint: string; rows: number }[] = [
  { id: 'handle', label: 'What should it handle for you?', hint: 'The jobs you want off your plate.', rows: 3 },
  { id: 'never', label: 'What should it never do?', hint: 'Lines it must not cross.', rows: 2 },
  { id: 'when', label: 'When should it message you first?', hint: 'For example: a note every school morning, or only when something is due.', rows: 2 },
  { id: 'tone', label: 'How should it talk to you?', hint: 'Short or detailed, formal or casual, any language.', rows: 2 },
  { id: 'examples', label: 'Messages you would send it', hint: 'Write two or three, exactly as you would type them.', rows: 3 },
  { id: 'last_week', label: 'A real task from last week', hint: 'What it was and what you did. One real example tells us more than a description.', rows: 3 },
];

function AgentCard({ agent, initial, onRemove }: { agent: AgentInfo; initial: Brief; onRemove?: () => void }) {
  const [answers, setAnswers] = useState(initial.answers);
  const [savedAt, setSavedAt] = useState(initial.updated_at);
  const [state, setState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const latest = useRef(answers);
  // Only the fields he edited are sent, so a chat answer in another field is never overwritten.
  const dirty = useRef<Set<Q | 'name'>>(new Set());
  const pending = () => { const b: Record<string, string> = {}; for (const q of dirty.current) b[q] = latest.current[q] ?? ''; return b; };

  const save = async () => {
    if (!dirty.current.size) return;
    const body = pending(); dirty.current = new Set();
    setState('saving');
    try {
      const r = await api<Brief>('PUT', `/api/t/agent-briefs/${agent.id}`, body);
      setSavedAt(r.updated_at); setState('idle'); setError(null);
    } catch (e) { for (const k of Object.keys(body)) dirty.current.add(k as Q | 'name'); setState('error'); setError((e as Error).message); }
  };
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); void save(); } }, []);
  // Closing or reloading the tab inside the save delay: send what he typed
  // with keepalive so it outlives the page.
  useEffect(() => {
    const flush = () => {
      if (!timer.current) return;
      clearTimeout(timer.current); timer.current = undefined;
      void fetch(`/api/t/agent-briefs/${agent.id}`, {
        method: 'PUT', keepalive: true, credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'tos' }, body: JSON.stringify(pending()),
      });
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, []);

  const change = (q: Q | 'name', v: string) => {
    const next = { ...latest.current, [q]: v };
    latest.current = next; setAnswers(next); dirty.current.add(q);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = undefined; void save(); }, 900);
  };

  const status = state === 'saving' ? 'Saving...' : state === 'error' ? 'Not saved' : savedAt ? `Saved ${new Date(savedAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}` : 'Not started';
  return (
    <Card title={agent.extra ? (answers.name?.trim() || 'New agent') : agent.name} sub={agent.sub} right={<span data-brief-status class="text-[12px] text-[var(--color-text-muted)]">{status}</span>}>
      <div data-brief={agent.id} class="flex flex-col gap-3.5">
        {agent.extra && (
          <label class="flex flex-col gap-1">
            <span class="text-[13px] font-medium text-[var(--color-text)]">What would you call it?</span>
            <span class="text-[12px] text-[var(--color-text-muted)]">A working name is fine, for example "Halaqa" or "Admin".</span>
            <input data-q="name" maxLength={60} value={answers.name ?? ''} onInput={(e) => change('name', (e.target as HTMLInputElement).value)}
              onBlur={() => { if (timer.current) { clearTimeout(timer.current); timer.current = undefined; void save(); } }}
              class="h-9 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 text-[13.5px] text-[var(--color-text)]" />
          </label>
        )}
        {QUESTIONS.map((q) => (
          <label key={q.id} class="flex flex-col gap-1">
            <span class="text-[13px] font-medium text-[var(--color-text)]">{q.label}</span>
            <span class="text-[12px] text-[var(--color-text-muted)]">{q.hint}</span>
            <textarea data-q={q.id} rows={q.rows} value={answers[q.id] ?? ''} onInput={(e) => change(q.id, (e.target as HTMLTextAreaElement).value)}
              onBlur={() => { if (timer.current) { clearTimeout(timer.current); timer.current = undefined; void save(); } }}
              class="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-2 text-[13.5px] text-[var(--color-text)] resize-y" />
          </label>
        ))}
        <ErrorLine error={error} />
        {onRemove && <div><Btn kind="danger" onClick={onRemove}>Remove this agent</Btn></div>}
      </div>
    </Card>
  );
}

export function AgentBriefs() {
  const [briefs, setBriefs] = useState<Record<string, Brief> | null>(null);
  const [extras, setExtras] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ answered: number; total: number } | null>(null);
  useEffect(() => { api<{ answered: number; total: number }>('GET', '/api/t/onboarding-progress').then(setProgress, () => {}); }, []);
  useEffect(() => {
    api<Record<string, Brief>>('GET', '/api/t/agent-briefs').then((b) => {
      setBriefs(b); setExtras(Object.keys(b).filter((k) => k.startsWith('x-')));
    }, (e) => setError((e as Error).message));
  }, []);
  const remove = async (id: string) => {
    if (!confirm('Remove this agent and its answers?')) return;
    try { await api('DELETE', `/api/t/agent-briefs/${id}`); setExtras((x) => x.filter((k) => k !== id)); } catch (e) { setError((e as Error).message); }
  };
  const empty: Brief = { answers: {}, updated_at: null };
  return (
    <TeachingPage title="My agents" breadcrumb="Tell Ali what each agent should do. It saves as you type; come back and change it any time.">
      <ErrorLine error={error} />
      {progress && (
        <div data-onboarding-progress class="text-[13px] text-[var(--color-text-muted)]">
          {progress.answered} of {progress.total} questions answered. Answers you give Manager on Telegram land here too.
        </div>
      )}
      {briefs && (
        <div class="grid gap-4 lg:grid-cols-2">
          {AGENTS.map((a) => <AgentCard key={a.id} agent={a} initial={briefs[a.id] ?? empty} />)}
          {extras.map((id) => (
            <AgentCard key={id} agent={{ id, name: 'New agent', sub: 'One you would like added.', extra: true }} initial={briefs[id] ?? empty} onRemove={() => void remove(id)} />
          ))}
        </div>
      )}
      {briefs && extras.length < MAX_EXTRA && (
        <div>
          <button type="button" data-add-agent onClick={() => setExtras((x) => [...x, newExtraId()])}
            class="h-10 px-4 rounded-xl border border-dashed border-[var(--color-border)] text-[13.5px] text-[var(--color-text)] inline-flex items-center gap-2 hover:bg-[var(--color-elevated)]">
            <Plus size={16} /> Add another agent
          </button>
          <div class="text-[12px] text-[var(--color-text-muted)] mt-1.5">Want help with something none of these cover? Add it and describe it the same way.</div>
        </div>
      )}
    </TeachingPage>
  );
}
